/**
 * Converts candidate data into structured text suitable for AI prompt injection
 *
 * @param {Object|Array} candidates - Single candidate object or array of candidates
 * @param {Object} options - Configuration options
 * @param {boolean} options.includeContact - Whether to include contact information (default: false)
 * @param {number} options.maxSkills - Maximum number of skills to include (default: 10)
 * @param {number} options.maxTags - Maximum number of tags to include (default: 5)
 * @param {number} options.maxRoles - Maximum number of recent roles to include (default: 3)
 * @param {number} options.maxEducation - Maximum number of education entries to include (default: 2)
 * @param {number} options.maxProjects - Maximum number of projects to include (default: 3)
 * @param {string} options.format - Output format: 'single' | 'list' | 'detailed' (default: 'single')
 * @returns {string} Formatted candidate information suitable for AI prompts
 */
export function candidateToPrompt(candidates, options = {}) {
  const {
    includeContact = false,
    maxSkills = 10,
    maxTags = 5,
    maxRoles = 3,
    maxEducation = 2,
    maxProjects = 3,
    format = "single",
  } = options;

  // Handle single candidate or array
  const candidateList = Array.isArray(candidates) ? candidates : [candidates];

  if (candidateList.length === 0) {
    return "No candidate information available for recruiting purposes";
  }

  const formatSingleCandidate = (candidate) => {
    if (!candidate) return "Candidate information not available";

    const parts = [];

    // Basic information
    if (candidate.name) {
      parts.push(`Candidate: ${candidate.name}`);
    }

    if (candidate.position_title || candidate.title) {
      parts.push(
        `Current/Target Role: ${candidate.position_title || candidate.title}`
      );
    }

    if (candidate.location) {
      parts.push(`Location: ${candidate.location}`);
    }

    // Contact information (optional)
    if (includeContact && candidate.email) {
      parts.push(`Contact: ${candidate.email}`);
    }

    // Experience and seniority
    if (candidate.years_experience) {
      parts.push(`Experience: ${candidate.years_experience} years`);
    } else if (candidate.seniority_level) {
      parts.push(`Level: ${candidate.seniority_level}`);
    }

    // Skills
    const skills = extractSkills(candidate.skills, maxSkills);
    if (skills.length > 0) {
      parts.push(`Skills: ${skills.join(", ")}`);
    }

    // Tags (additional categorization)
    const tags = extractTags(candidate.tags, maxTags);
    if (tags.length > 0) {
      parts.push(`Tags: ${tags.join(", ")}`);
    }

    // Work history
    const workHistory = extractWorkHistory(candidate.roles, maxRoles);
    if (workHistory.length > 0) {
      parts.push(`Recent Experience: ${workHistory.join(", ")}`);
    }

    // Education
    const education = extractEducation(candidate.education, maxEducation);
    if (education.length > 0) {
      parts.push(`Education: ${education.join(", ")}`);
    }

    // Projects
    const projects = extractProjects(candidate.projects, maxProjects);
    if (projects.length > 0) {
      parts.push(`Notable Projects: ${projects.join(", ")}`);
    }

    // Compensation and availability
    if (candidate.salary_expectation) {
      parts.push(`Salary Expectation: ${candidate.salary_expectation}`);
    }

    if (candidate.availability || candidate.status) {
      parts.push(`Status: ${candidate.availability || candidate.status}`);
    }

    return parts.length > 0
      ? parts.join(" | ")
      : "Basic candidate information available";
  };

  // Format output based on requested format
  if (format === "list" && candidateList.length > 1) {
    const candidateDescriptions = candidateList.map(
      (candidate, index) => `${index + 1}. ${formatSingleCandidate(candidate)}`
    );
    return `Multiple Candidates:\n${candidateDescriptions.join("\n")}`;
  }

  if (format === "detailed" && candidateList.length > 1) {
    const candidateDescriptions = candidateList.map(formatSingleCandidate);
    return `Candidate Pool Information: ${candidateDescriptions.join(" || ")}`;
  }

  // Single candidate or simple format
  if (candidateList.length === 1) {
    return `Candidate Profile Information: ${formatSingleCandidate(
      candidateList[0]
    )}`;
  }

  // Multiple candidates, single format
  const candidateDescriptions = candidateList.map(formatSingleCandidate);
  return `Multiple Candidates: ${candidateDescriptions.join(" || ")}`;
}

/**
 * Extract and normalize skills from various data formats
 * @private
 */
function extractSkills(skills, maxSkills) {
  if (!skills || !Array.isArray(skills)) return [];

  return skills
    .map((skill) => {
      if (typeof skill === "string") return skill;
      return skill.name || skill.title || skill.skill;
    })
    .filter(Boolean)
    .slice(0, maxSkills);
}

/**
 * Extract and normalize tags from various data formats
 * @private
 */
function extractTags(tags, maxTags) {
  if (!tags || !Array.isArray(tags)) return [];

  return tags
    .map((tag) => {
      if (typeof tag === "string") return tag;
      return tag.name || tag.title;
    })
    .filter(Boolean)
    .slice(0, maxTags);
}

/**
 * Extract and format work history
 * @private
 */
function extractWorkHistory(roles, maxRoles) {
  if (!roles || !Array.isArray(roles)) return [];

  return roles
    .slice(0, maxRoles)
    .map((role) => {
      const title = role.title || role.position || "Role";
      const company = role.company || role.employer || "Company";
      return `${title} at ${company}`;
    })
    .filter(
      (role) => !role.includes("undefined") && !role.includes("Role at Company")
    );
}

/**
 * Extract and format education information
 * @private
 */
function extractEducation(education, maxEducation) {
  if (!education || !Array.isArray(education)) return [];

  return education
    .slice(0, maxEducation)
    .map((edu) => {
      const degree = edu.degree || edu.qualification || "Degree";
      const institution =
        edu.institution || edu.school || edu.university || "Institution";
      return `${degree} from ${institution}`;
    })
    .filter(
      (edu) =>
        !edu.includes("undefined") && !edu.includes("Degree from Institution")
    );
}

/**
 * Extract and format project information
 * @private
 */
function extractProjects(projects, maxProjects) {
  if (!projects || !Array.isArray(projects)) return [];

  return projects
    .slice(0, maxProjects)
    .map((project) => project.title || project.name || project.project_name)
    .filter(Boolean);
}

/**
 * Create a context-specific prompt for different recruiting scenarios
 *
 * @param {Object|Array} candidates - Candidate data
 * @param {string} context - The context: 'highlights', 'summary', 'introduction', 'outreach', 'comparison'
 * @param {Object} options - Additional options for candidateToPrompt
 * @returns {string} Context-specific prompt text
 */
export function createRecruitingPrompt(candidates, context, options = {}) {
  const candidateInfo = candidateToPrompt(candidates, options);

  const contextPrompts = {
    highlights: `${candidateInfo}. This is for candidate highlights - a brief, impactful summary of the candidate's most notable achievements, skills, and career highlights. Focus on what makes them stand out for recruiting purposes. Keep it concise and compelling.`,

    summary: `${candidateInfo}. This is for a candidate summary - a concise professional summary (2-3 sentences) that introduces the candidate. Include their role, key expertise, and what makes them valuable. Perfect for initial candidate presentations or brief profiles.`,

    introduction: `${candidateInfo}. This is for a detailed candidate introduction - a comprehensive professional summary (4-6 sentences) providing detailed insight into the candidate's background, experience, achievements, and potential value to employers. Include specific examples of expertise, career progression, and what makes them exceptional. Suitable for detailed candidate profiles and comprehensive presentations.`,

    outreach: `${candidateInfo}. This is for recruiting outreach content. Focus on personalizing the message based on the candidate's background, being engaging and professional while highlighting relevant opportunities.`,

    comparison: `${candidateInfo}. This is for comparing and contrasting multiple candidates, highlighting their unique strengths and how they might fit different roles or requirements.`,

    general: candidateInfo,
  };

  return contextPrompts[context] || contextPrompts.general;
}
